import { find, isEmpty } from "lodash";

import { type SessionUserFragment } from "@/gql/graphql";

import type { Feature as ProjectFeature } from "../components/admin/projects/settings/Features";
import type { Feature } from "../utils/admin";

interface ProjectLikeInterface {
  attrs?: {
    features?: string[];
  };
}

type ProjectLikeType = (ProjectRef & { attrs: { features: string[] } }) | ProjectLikeInterface;
type FeatureLikeType = Feature | ProjectFeature;

export type PermissionUserType = UserRef & Pick<SessionUserFragment, "role" | "flags" | "customerId" | "teamLeader">;
export type PermissionCustomerType = CustomerRefWithIdentifier & {
  // TODO: type this better
  attrs: { features?: string[]; [key: string]: any };
  customerAdministrators: Array<UserRef>;
};

export const customerFeatureEnabled = (
  customer: Nilable<PermissionCustomerType>,
  feature: FeatureLikeType | string,
): boolean => !isEmpty(find(customer?.attrs?.features || [], (f) => f === feature));

export const projectFeatureEnabled = (project: Nilable<ProjectLikeType>, feature: FeatureLikeType): boolean =>
  !isEmpty(find(project?.attrs?.features || [], (f) => f === feature));

export const userFeatureEnabled = (user: Nilable<PermissionUserType>, feature: FeatureLikeType): boolean =>
  !isEmpty(find(user?.flags || [], (f) => f === feature)) || isRoot(user);

export const featureEnabled = (
  user: Nilable<PermissionUserType>,
  project: Nilable<ProjectLikeType>,
  feature: FeatureLikeType,
): boolean => projectFeatureEnabled(project, feature) && userFeatureEnabled(user, feature);

export const isRoot = (user: Nilable<PermissionUserType>): boolean => !!user && user.role.toUpperCase() === "ROOT";
export const isAdmin = (user: Nilable<PermissionUserType>): boolean =>
  !!user && (isRoot(user) || user.role.toUpperCase() === "ADMIN");
export const isCustomerAdmin = (user: Nilable<PermissionUserType>, customer: PermissionCustomerType): boolean =>
  !!user &&
  (isAdmin(user) ||
    (user.role.toUpperCase() === "CUSTOMER_ADMIN" && customer.id === user.customerId) ||
    (!!customer && !!find(customer.customerAdministrators, (u) => u.id === user.id)));
export const isStaffAdmin = (user: Nilable<PermissionUserType>): boolean =>
  !!user && (isRoot(user) || (isAdmin(user) && userFeatureEnabled(user, "employees/write")));

type TargetUserType = Nilable<UserRef & { role: UserRole }>;

export const userAndTargetAreAdmin = (user: Nilable<PermissionUserType>, targetUser: TargetUserType): boolean =>
  !!user &&
  !!targetUser &&
  user.role.toUpperCase() === "ADMIN" &&
  ["ROOT", "ADMIN"].includes(targetUser.role.toUpperCase()) &&
  user.id !== targetUser.id;
export const isTermitel = (user: Nilable<PermissionUserType>): boolean => user?.customerId === "1";

export const onlyRootForTermitelVerwaltung = (
  user: Nilable<PermissionUserType>,
  customer: Nilable<PermissionCustomerType>,
  project: Nilable<ProjectRef & { identifier: string }>,
): boolean => {
  if (!customer || !project || !user) {
    return false;
  }

  return customer.identifier === "termitel" && project.identifier === "verwaltung" ? isRoot(user) : true;
};
